import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '.';
import type { PromptGroup, Prompt } from '@server/prompts';
import { undoable } from './undoable';

export interface PromptLibraryState {
  promptGroups?: PromptGroup[];
  prompts?: Prompt[];
  promptCountByGroupId: Record<string, number>;
  isLoadingPrompts: boolean;
  selectedPromptGroupId: string | null;
  selectedPrompt: Prompt | null;
  isLoading: boolean;
}

const initialState: PromptLibraryState = {
  promptCountByGroupId: {},
  selectedPromptGroupId: null,
  selectedPrompt: null,
  isLoading: false,
  isLoadingPrompts: false,
};

const computePromptCountByGroupId = (prompts: Prompt[]): Record<string, number> => {
  const countMap: Record<string, number> = {};
  for (const prompt of prompts) {
    if (prompt.promptGroupId) {
      countMap[prompt.promptGroupId] = (countMap[prompt.promptGroupId] || 0) + 1;
    }
  }
  return countMap;
};

export const promptLibrarySlice = createSlice({
  name: 'promptLibrary',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },

    setLoadingPrompts: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoadingPrompts = action.payload.isLoading;
    },

    setPromptGroups: (state, action: PayloadAction<{ promptGroups: PromptGroup[] }>) => {
      state.promptGroups = action.payload.promptGroups;
      state.isLoading = false;
    },

    setPrompts: (state, action: PayloadAction<{ prompts: Prompt[] }>) => {
      state.prompts = action.payload.prompts;
      state.promptCountByGroupId = computePromptCountByGroupId(action.payload.prompts);
      state.isLoadingPrompts = false;
    },

    addPromptGroup: (state, action: PayloadAction<{ promptGroup: PromptGroup }>) => {
      const { promptGroup } = action.payload;
      if (!state.promptGroups) {
        state.promptGroups = [];
      }
      state.promptGroups.unshift(promptGroup);
    },

    updatePromptGroup: (
      state,
      action: PayloadAction<{
        id: string;
        update: Partial<PromptGroup>;
      }>,
    ) => {
      const { id, update } = action.payload;
      if (state.promptGroups) {
        const groupIndex = state.promptGroups.findIndex((group) => group.id === id);
        if (groupIndex !== -1) {
          Object.assign(state.promptGroups[groupIndex], update);
        }
      }
    },

    deletePromptGroup: (state, action: PayloadAction<{ groupId: string }>) => {
      if (state.promptGroups) {
        state.promptGroups = state.promptGroups.filter(
          (group) => group.id !== action.payload.groupId,
        );
      }
      // Remove prompts connected to this group
      if (state.prompts) {
        state.prompts = state.prompts.filter(
          (prompt) => prompt.promptGroupId !== action.payload.groupId,
        );
      }

      if (state.promptCountByGroupId[action.payload.groupId]) {
        delete state.promptCountByGroupId[action.payload.groupId];
      }
    },

    addPrompt: (state, action: PayloadAction<{ prompt: Prompt }>) => {
      const { prompt } = action.payload;
      if (!state.prompts) {
        state.prompts = [];
      }
      state.prompts.unshift(prompt);
      if (prompt.promptGroupId) {
        state.promptCountByGroupId[prompt.promptGroupId] =
          (state.promptCountByGroupId[prompt.promptGroupId] || 0) + 1;
      }
    },

    updatePrompt: (
      state,
      action: PayloadAction<{
        promptId: string;
        update: Partial<Prompt>;
      }>,
    ) => {
      const { promptId, update } = action.payload;
      if (state.prompts) {
        const promptIndex = state.prompts.findIndex((prompt) => prompt.id === promptId);
        if (promptIndex !== -1) {
          Object.assign(state.prompts[promptIndex], update);
        }
      }
    },

    deletePrompt: (state, action: PayloadAction<{ promptId: string }>) => {
      const { promptId } = action.payload;
      if (state.prompts) {
        const deletedPrompt = state.prompts.find((prompt) => prompt.id === promptId);
        if (deletedPrompt?.promptGroupId) {
          state.promptCountByGroupId[deletedPrompt.promptGroupId] = Math.max(
            0,
            (state.promptCountByGroupId[deletedPrompt.promptGroupId] || 0) - 1,
          );
        }
        state.prompts = state.prompts.filter((prompt) => prompt.id !== promptId);
      }
    },

    setSelectedPromptGroupId: (state, action: PayloadAction<{ groupId: string | null }>) => {
      state.selectedPromptGroupId = action.payload.groupId;
    },

    setSelectedPrompt: (state, action: PayloadAction<{ prompt: Prompt | null }>) => {
      state.selectedPrompt = action.payload.prompt;
    },
    resetState: (state) => {
      state.selectedPrompt = null;
      state.selectedPromptGroupId = null;
      state.isLoading = false;
    },
  },
});

export const selectPromptGroups = (state: RootState) => state.promptLibrary.promptGroups;
export const selectPrompts = (state: RootState) => state.promptLibrary.prompts;
export const selectPromptCountByGroupId = (state: RootState) =>
  state.promptLibrary.promptCountByGroupId;

export const selectSelectedPromptGroupId = (state: RootState) =>
  state.promptLibrary.selectedPromptGroupId;
export const selectSelectedPrompt = (state: RootState) => state.promptLibrary.selectedPrompt;
export const selectIsLoading = (state: RootState) => state.promptLibrary.isLoading;
export const selectIsLoadingPrompts = (state: RootState) => state.promptLibrary.isLoadingPrompts;

export const {
  setLoading,
  setLoadingPrompts,
  setPromptGroups,
  setPrompts,
  addPromptGroup,
  updatePromptGroup,
  deletePromptGroup,
  addPrompt,
  updatePrompt,
  deletePrompt,
  setSelectedPromptGroupId,
  setSelectedPrompt,
  resetState,
} = promptLibrarySlice.actions;

export default undoable(promptLibrarySlice.reducer);
